import {formatPercent} from '@deliveriesondemand/dod-utils';
import {formatMoney} from 'accounting';
import {Component, Fragment} from 'react';
import injectSheet from 'react-jss';

import BypassAuthButton from '-/components/fields/BypassAuthButton';
import Button from '-/components/general/Button';
import {important, rem} from '-/styles/utils';

/** Below this the two-column tables and the headings have to step down. */
const narrow = content => ({
    '@media all and (max-width: 448px)': content
});

const styles = theme => ({
    /*
     * EVERY HEADING SETS ITS OWN LINE HEIGHT, deliberately.
     *
     * These used to set `fontSize` alone and inherit the line height from `global.js`,
     * which carries `...smallScreen({lineHeight: rem(16)})` -- and `smallScreen` is a
     * `max-device-height` query, so on a short SCREEN (not a short window) a 28px
     * heading was being given a 16px line height. Anything that wrapped then collided
     * with whatever followed it, which is what made the desktop page look broken while
     * mobile looked fine. Specificity does the work here: `.root h2` beats a bare `h2`
     * whatever media query it sits in, so no `!important` is needed.
     */
    root: {
        marginBottom: rem(theme.greetingSpacing),
        padding: `0 ${rem(16)}`,
        color: theme.colors.greeting,
        textAlign: 'center',
        fontWeight: 'bold',
        '& h1': {
            marginBottom: rem(24),
            fontSize: rem(28),
            fontWeight: 'bold',
            lineHeight: rem(36)
        },
        '& h2': {
            marginBottom: rem(6),
            fontSize: rem(24),
            fontWeight: 'bold',
            lineHeight: rem(30)
        },
        ...narrow({
            '& h1': {
                marginBottom: rem(16),
                fontSize: rem(22),
                lineHeight: rem(28)
            },
            '& h2': {
                fontSize: rem(19),
                lineHeight: rem(25)
            }
        }),
        '& hr': {
            width: '90%',
            margin: `${rem(24)} auto`,
            border: 'none',
            borderTop: `2px solid ${theme.colors.primary}`
        }
    },

    /*
     * The saving was one `<h2>` with a `<br>` in it, so the label and the figure shared a
     * single line height -- the pair that actually collided. Two elements now, each with
     * its own metrics, and the figure gets the size it deserves.
     */
    savings: {
        marginBottom: rem(4)
    },
    savingsAmount: {
        margin: important(0),
        color: 'green',
        fontSize: important(rem(34)),
        fontWeight: 'bold',
        lineHeight: important(rem(40)),
        textAlign: 'center',
        ...narrow({
            fontSize: important(rem(26)),
            lineHeight: important(rem(32))
        })
    },
    green: {
        color: 'green'
    },
    red: {
        color: 'red'
    },
    preapproval: {
        width: '100%',
        marginBottom: 15,
        padding: 5,
        fontSize: '1.75em',
        borderCollapse: 'collapse',
        '& td': {
            width: '50%',
            textAlign: 'left',
            padding: 15
        },
        '& td:first-child': {
            textAlign: 'right',
            borderRight: `2px solid ${theme.colors.primary}`
        },
        '@media all and (max-width: 448px)': {
            fontSize: '1.2em',
            '& td': {
                padding: '6px 15px'
            }
        }
    },
    section: {
        marginBottom: rem(20)
    },
    offers: {
        margin: `0 0 ${rem(20)}`,
        padding: 0,
        listStyle: 'none',
        '& li': {
            marginBottom: rem(12),
            fontSize: rem(18),
            lineHeight: rem(24)
        },
        '& strong': {
            display: 'block',
            color: 'green',
            fontSize: rem(22),
            lineHeight: rem(28)
        },
        ...narrow({
            '& li': {
                fontSize: rem(15),
                lineHeight: rem(20)
            },
            '& strong': {
                fontSize: rem(18),
                lineHeight: rem(24)
            }
        })
    },
    disclaimer: {
        marginTop: rem(16),
        fontSize: rem(12),
        fontWeight: 'normal',
        lineHeight: rem(16)
    },
    button: {
        marginTop: rem(8),
        ...narrow({
            fontSize: '.75em',
            letterSpacing: 1,
            margin: 0,
            fontWeight: 'normal'
        })
    }
});

class CompactLanding extends Component {
    render() {
        const {
            auth: {
                authInfo,
                authType,
                showBypassAuthButton
            } = {},
            classes = {},
            dealInfo: {
                cashBonus,
                deferredDays,
                existingRate,
                giftAmount,
                lifeTimeSavings,
                newRate,
                spinToWinGiftAmount
            } = {},
            name,
            onNextClick,
            // The member's first name (buyerInfo.shortName), not the creditor's.
            shortName: firstName = name,
            // Was read as a bare `status`, which resolved to `window.status` -- an empty
            // string -- so the submitted-member label below could never apply.
            status,
            stips: {
                allStipsVerifiedForApplicant
            } = {}
        } = this.props;

        const showRates = newRate < existingRate;
        // Gated on the rates too: `lifeTimeSavings` is not always a saving, so it is only
        // shown beside a rate that actually improved.
        const showLifeTimeSavings = showRates && lifeTimeSavings >= 1000;
        const greeting = firstName ? `${firstName}'s Pre-Approved Auto Refinance Offer` : 'Your Pre-Approved Auto Refinance Offer';
        const buttonText = status === 'SUBMITTED' ? 'Review your information' : 'Continue With My Offer';
        // The spin prize is assigned to the offer before the drop goes out, so its presence
        // says this member gets a spin. The amount itself is never shown -- it is the prize.
        const showSpin = spinToWinGiftAmount > 0;
        const showSpecialOffers = deferredDays > 0 || giftAmount > 0 || cashBonus > 0 || showSpin;

        return (
            <section className={classes.root}>
                <h1>{greeting}</h1>
                {showRates ? (
                    <table className={classes.preapproval}>
                        <tbody>
                            <tr>
                                <td>CURRENT RATE</td>
                                <td className={classes.red}>{formatPercent(existingRate, 'dynamic')}</td>
                            </tr>
                            <tr>
                                <td>YOUR NEW RATE</td>
                                <td className={classes.green}>{`${formatPercent(newRate, 'dynamic')} APR`}</td>
                            </tr>
                        </tbody>
                    </table>
                ) : (
                    <p>Continue below to review your refinance offer.</p>
                )}
                {showLifeTimeSavings && (
                    <div className={classes.savings}>
                        <h2>ESTIMATED INTEREST SAVINGS</h2>
                        <p className={classes.savingsAmount}>
                            {formatMoney(lifeTimeSavings)}
                        </p>
                    </div>
                )}
                <hr />
                {showSpecialOffers && (
                    <Fragment>
                        <h2 className={classes.green}>YOUR SPECIAL OFFERS</h2>
                        <ul className={classes.offers}>
                            {deferredDays > 0 && (
                                <li><strong>{`${deferredDays} DAYS`}</strong>No Car Payment</li>
                            )}
                            {giftAmount > 0 && (
                                <li>
                                    <strong>{`${formatMoney(giftAmount, '$', 0)} GIFT CARD`}</strong>
                                    When Your Eligible Refinance Is Funded
                                </li>
                            )}
                            {cashBonus > 0 && (
                                <li><strong>{`${formatMoney(cashBonus, '$', 0)} CASH BONUS`}</strong>When Your Eligible Refinance Is Funded</li>
                            )}
                            {showSpin && (
                                <li><strong>BONUS REWARD SPIN</strong>Chance to Win Up to $1,000 Extra!</li>
                            )}
                        </ul>
                    </Fragment>
                )}
                {!allStipsVerifiedForApplicant && !showBypassAuthButton && (
                    <Button
                        width={95}
                        className={classes.button}
                        tag="a"
                        href="#"
                        onClick={onNextClick}
                        title={buttonText}
                        role="button">
                        {buttonText}
                    </Button>
                )}
                {showBypassAuthButton && (
                    <BypassAuthButton
                        authType={authType}
                        authInfo={authInfo}
                        buttonText={buttonText} />
                )}
                <p className={classes.disclaimer}>
                    Offer subject to verification, final approval, and applicable promotional terms.
                </p>
            </section>
        );
    }
}

export default injectSheet(styles)(CompactLanding);
